import { useExercisePicker } from 'app/ui/components/molecules/picker/exercise/util';
import type React from 'react';
import { useEffect, useRef, useState } from 'react';
import styled, { useTheme } from 'styled-components';

import ChevronDown from 'app/ui/components/atoms/icons/chevron-down';
import ChevronUp from 'app/ui/components/atoms/icons/chevron-up';
import RoundTick from 'app/ui/components/atoms/icons/round-tick';

import type { ContentItemData } from 'app/ui/components/molecules/content-item-areas-wrapper';
import {
  DropdownButton,
  DropdownContainer,
  DropdownItem,
  DropdownList,
  Label,
} from 'app/ui/components/molecules/picker';
import { useParams } from 'react-router-dom';
import { trackEvent } from 'src/app/utils/analytics/tracker';
import { useWorkoutDataContext } from 'src/app/pages/workout/context';

type ExercisePickerProps = {
  exercises: ContentItemData[];
  exercisePicked: ContentItemData | null;
  pickerId: string;
  onChange: (exercise: ContentItemData) => void;
  onClick?: (event: React.MouseEvent<HTMLDivElement>) => void;
};

const StyledDropdownButton = styled(DropdownButton)`
  gap: ${({ theme: { spacing } }) => spacing(1)};
  height: ${({ theme: { spacing } }) => spacing(7)};
  padding: 0 ${({ theme: { spacing } }) => spacing(2)};
`;

const ExercisePicker: React.FC<ExercisePickerProps> = ({ exercises, exercisePicked, pickerId, onChange, onClick }) => {
  const theme = useTheme();
  const instanceIdRef = useRef(pickerId);
  const dropdownId = instanceIdRef.current;
  const { id } = useParams();
  const { workout } = useWorkoutDataContext();

  const { openExercisePickerId, setOpenExercisePickerId } = useExercisePicker();

  const [isLocalOpen, setIsLocalOpen] = useState(false);

  useEffect(() => setIsLocalOpen(openExercisePickerId === dropdownId), [openExercisePickerId, dropdownId]);

  const toggleDropdown = () => {
    setIsLocalOpen(!isLocalOpen);

    if (isLocalOpen) {
      setOpenExercisePickerId(null);
    } else {
      setOpenExercisePickerId(dropdownId);
    }
  };

  const selectExercise = (exercise: ContentItemData) => {
    onChange(exercise);
    setIsLocalOpen(false);
    setOpenExercisePickerId(null);
    if (id && exercisePicked && workout) {
      trackEvent('exercise_swapped', {
        workout_id: workout.id,
        workout_name: workout.name,
        exercise_from: exercisePicked.title,
        exercise_to: exercise.title,
      });
    }
  };

  const {
    colors: { focoltone, white },
  } = theme;

  return (
    <DropdownContainer onClick={onClick} data-testid="exercise-picker-container">
      <StyledDropdownButton onClick={toggleDropdown}>
        <Label $isOpen={isLocalOpen}>{exercisePicked?.title}</Label>
        {isLocalOpen ? (
          <ChevronUp color={isLocalOpen ? focoltone : white} />
        ) : (
          <ChevronDown color={isLocalOpen ? focoltone : white} />
        )}
      </StyledDropdownButton>
      {isLocalOpen && (
        <DropdownList>
          {exercises.map((exercise) => (
            <DropdownItem key={exercise.id} onClick={() => selectExercise(exercise)} data-testid="exercise-picker-item">
              {exercise.title}
              {exercise.id === exercisePicked?.id && <RoundTick color={white} />}
            </DropdownItem>
          ))}
        </DropdownList>
      )}
    </DropdownContainer>
  );
};

export default ExercisePicker;
